import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Link } from 'react-router-dom';
import DateCell from '../components/DateCell';
import DirectionCell from '../components/DirectionCell';
import TickerCell from '../components/TickerCell';
import FilterStatusCell from '../components/FilterStatusCell';
import { Trigger } from '../backend/interfaces';
import { fetchTriggersData } from '../backend/fetchs';

const TriggersPage: React.FC = () => {
  const [data, setData] = useState<Trigger[]>([]);
  const [filteredData, setFilteredData] = useState<Trigger[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [showFilterPanel, setShowFilterPanel] = useState(false);
  const [page, setPage] = useState(1);
  const [hasMore, setHasMore] = useState(true);
  const [filters, setFilters] = useState({
    volumeFilter: false,
    rsiFilter: false,
    engulfingFilter: false,
    candlestickPatternFilter: false,
    tickerFilter: ''
  });
  const observer = useRef<IntersectionObserver | null>(null);
  const hasMoreRef = useRef<boolean>(true);
  const lastIntersectionTime = useRef<number>(0);
  const fetchDataRef = useRef<(() => Promise<void>) | null>(null);
  const currentFetchPage = useRef<number>(0);
  const maxPage = 10;

  // Helper function to ensure unique triggers by ID
  const getUniqueTriggersById = (triggers: Trigger[]): Trigger[] => {
    const seen = new Set<number>();
    return triggers.filter(trigger => {
      if (seen.has(trigger.id)) {
        return false;
      }
      seen.add(trigger.id);
      return true;
    });
  };

    const fetchData = useCallback(async () => {
    // Don't fetch if already loading (except for initial load)
    if (loading && data.length > 0) {
      return;
    }

    // Don't fetch if we're already fetching this page
    if (currentFetchPage.current === page) {
      return;
    }

    // Don't fetch if we've reached page 5 or no more data
    if (page > maxPage) {
      hasMoreRef.current = false;
      setHasMore(false);
      return;
    }

    if (!hasMoreRef.current && page > 1) {
      return;
    }

    try {
      setLoading(true);
      currentFetchPage.current = page; // Mark this page as being fetched
      console.log(`🔄 Fetching page ${page}`);
      const result = await fetchTriggersData(page);
      
      if (page === 1) {
        // First page - replace data and ensure uniqueness
        const uniqueData = getUniqueTriggersById(result.data);
        setData(uniqueData);
      } else {
        // Subsequent pages - append data, but avoid duplicates
        setData(prevData => {
          const existingIds = new Set(prevData.map(item => item.id));
          const newItems = result.data.filter(item => !existingIds.has(item.id));
          const combinedData = [...prevData, ...newItems];
          return getUniqueTriggersById(combinedData);
        });
      }

      // Check if we should continue fetching
      const shouldStop = page >= maxPage || result.data.length === 0;
      hasMoreRef.current = !shouldStop;
      setHasMore(!shouldStop);

      if (!shouldStop) {
        setPage(prevPage => prevPage + 1);
      }

      setError(null);
    } catch (err) {
      setError('Error fetching data');
      console.error('Error fetching triggers data:', err);
      hasMoreRef.current = false;
      setHasMore(false);
    } finally {
      setLoading(false);
      currentFetchPage.current = 0; // Clear the fetch tracking
    }
  }, [page, loading, data.length]);

  // Keep a stable reference to fetchData for intersection observer
  fetchDataRef.current = fetchData;

  // Initial data fetch only
  useEffect(() => {
    fetchData();
  }, []); // Empty dependency array for initial load only

  const lastElementRef = useCallback((node: HTMLTableRowElement) => {
    if (loading) return;
    if (observer.current) observer.current.disconnect();
    
    observer.current = new IntersectionObserver(entries => {
      if (entries[0].isIntersecting && hasMore) {
        const now = Date.now();
        // Debounce: only trigger if 500ms have passed since last intersection
        if (now - lastIntersectionTime.current > 500) {
          lastIntersectionTime.current = now;
          fetchDataRef.current?.();
        }
      }
    });
    
    if (node) observer.current.observe(node);
  }, [loading, hasMore]); // Removed fetchData dependency to prevent recreation

  // Auto-apply filters when data or filters change
  useEffect(() => {
    
    let filtered = [...data];

    if (filters.volumeFilter) {
      filtered = filtered.filter(item => item.filters?.volume?.status === true);
    }
    if (filters.rsiFilter) {
      filtered = filtered.filter(item => item.filters?.rsi?.status === true);
    }
    if (filters.engulfingFilter) {
      filtered = filtered.filter(item => item.filters?.engulfing?.status === true);
    }
    if (filters.candlestickPatternFilter) {
      filtered = filtered.filter(item => item.filters?.candlestick_type?.status === true);
    }
    if (filters.tickerFilter.trim()) {
      filtered = filtered.filter(item => 
        item.ticker?.description?.toLowerCase().includes(filters.tickerFilter.toLowerCase())
      );
    }

    setFilteredData(filtered);
  }, [data, filters]);

  const handleFilterChange = (filterName: keyof typeof filters) => {
    setFilters(prev => {
      const newFilters = {
        ...prev,
        [filterName]: !prev[filterName]
      };
      return newFilters;
    });
  };

  const handleTickerFilterChange = (value: string) => {
    setFilters(prev => ({
      ...prev,
      tickerFilter: value
    }));
  };

  if (error && data.length === 0) {
    return (
      <div className="text-center">
        <div className="text-red-500 text-xl mb-4">{error}</div>
        <button
          onClick={() => {
            setPage(1);
            setData([]);
            hasMoreRef.current = true;
            setHasMore(true);
            setError(null);
            lastIntersectionTime.current = 0; // Reset debounce timer
            currentFetchPage.current = 0; // Reset fetch tracking
            setTimeout(() => fetchData(), 100); // Small delay to ensure state is reset
          }}
          className="bg-blue-500 hover:bg-blue-700 text-white font-bold py-2 px-4 rounded"
        >
          Retry
        </button>
      </div>
    );
  }

  return (
    <div className="relative">
      <div className="bg-white shadow-md rounded-lg overflow-hidden">
        <div className="px-6 py-4 border-b border-gray-200 flex justify-between items-center">
          <h2 className="text-2xl font-bold text-gray-800">Latest Triggers</h2>
          <button
            onClick={() => setShowFilterPanel(true)}
            className="bg-blue-500 hover:bg-blue-700 text-white font-bold py-2 px-4 rounded flex items-center space-x-2"
          >
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 4a1 1 0 011-1h16a1 1 0 011 1v2.586a1 1 0 01-.293.707l-6.414 6.414a1 1 0 00-.293.707V17l-4 4v-6.586a1 1 0 00-.293-.707L3.293 7.293A1 1 0 013 6.586V4z" />
            </svg>
            <span>Filters</span>
          </button>
        </div>
      
      <div className="overflow-x-auto">
        <table className="min-w-full divide-y divide-gray-200">
          <thead className="bg-gray-50">
            <tr>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                ID
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Ticker
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Decision
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Detected
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Volume Filter
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                RSI Filter
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Engulfing Filter
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Patterns Filter
              </th>
            </tr>
          </thead>
          <tbody className="bg-white divide-y divide-gray-200">
            {filteredData.map((item, index) => (
              <tr 
                key={item.id} 
                ref={filteredData.length === index + 1 ? lastElementRef : null}
                className="hover:bg-gray-50"
              >
                <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                  <Link
                    to={`/triggers/${item.id}`}
                    className="text-blue-600 hover:text-blue-900 font-medium hover:underline"
                  >
                    {item.id}
                  </Link>
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                  <TickerCell ticker={item.ticker.description} />
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                  <DirectionCell direction={item.side.description} />
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                  <DateCell timestamp={item.created_at} />
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                  <FilterStatusCell status={item.filters.volume.status} />
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                  <FilterStatusCell status={item.filters.rsi.status} />
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                  <FilterStatusCell status={item.filters.engulfing.status} />
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                  <FilterStatusCell status={item.filters.candlestick_type.status} />
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {loading && (
        <div className="flex justify-center items-center py-4">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-500"></div>
        </div>
      )}

      {!hasMore && page > 1 && (
        <div className="text-center py-4 border-t border-gray-200">
          <div className="text-gray-500 text-sm">
            {page === maxPage ? 
              `Maximum of ${maxPage} pages reached. No more data to load.` : 
              'No more data to load.'
            }
          </div>
        </div>
      )}

      {error && data.length > 0 && (
        <div className="text-center py-4">
          <div className="text-red-500 text-sm mb-2">{error}</div>
          <button
            onClick={() => {
              setPage(1);
              setData([]);
              hasMoreRef.current = true;
              setHasMore(true);
              setError(null);
              lastIntersectionTime.current = 0; // Reset debounce timer
              currentFetchPage.current = 0; // Reset fetch tracking
              setTimeout(() => fetchData(), 100); // Small delay to ensure state is reset
            }}
            className="bg-blue-500 hover:bg-blue-700 text-white font-bold py-2 px-4 rounded text-sm"
          >
            Retry
          </button>
        </div>
      )}
      </div>

      {/* Filter Panel */}
      {showFilterPanel && (
        <div className="fixed inset-0 z-50 overflow-hidden">
          <div className="absolute inset-0 bg-black bg-opacity-50" onClick={() => setShowFilterPanel(false)}></div>
          <div className="absolute right-0 top-0 h-full w-80 bg-white shadow-xl">
            <div className="flex flex-col h-full">
              {/* Header */}
              <div className="px-6 py-4 border-b border-gray-200 flex justify-between items-center">
                <h3 className="text-lg font-medium text-gray-900">Filters</h3>
                <button
                  onClick={() => setShowFilterPanel(false)}
                  className="text-gray-400 hover:text-gray-600"
                >
                  <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>

              {/* Filter Options */}
              <div className="px-6 py-4 space-y-4">
                {/* Ticker Filter Input */}
                <div className="mb-4">
                  <label htmlFor="tickerFilter" className="block text-sm font-medium text-gray-700 mb-2">
                    Filter by Ticker
                  </label>
                  <input
                    id="tickerFilter"
                    type="text"
                    placeholder="e.g. BTCUSDT, ETHUSDT..."
                    value={filters.tickerFilter}
                    onChange={(e) => handleTickerFilterChange(e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 text-sm"
                  />
                </div>

                <div className="space-y-3">
                  <div className="flex items-center">
                    <input
                      id="volumeFilter"
                      type="checkbox"
                      checked={filters.volumeFilter}
                      onChange={() => handleFilterChange('volumeFilter')}
                      className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                    />
                    <label htmlFor="volumeFilter" className="ml-3 text-sm font-medium text-gray-700">
                      Volume Filter
                    </label>
                  </div>

                  <div className="flex items-center">
                    <input
                      id="rsiFilter"
                      type="checkbox"
                      checked={filters.rsiFilter}
                      onChange={() => handleFilterChange('rsiFilter')}
                      className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                    />
                    <label htmlFor="rsiFilter" className="ml-3 text-sm font-medium text-gray-700">
                      RSI Filter
                    </label>
                  </div>

                  <div className="flex items-center">
                    <input
                      id="engulfingFilter"
                      type="checkbox"
                      checked={filters.engulfingFilter}
                      onChange={() => handleFilterChange('engulfingFilter')}
                      className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                    />
                    <label htmlFor="engulfingFilter" className="ml-3 text-sm font-medium text-gray-700">
                      Engulfing Filter
                    </label>
                  </div>

                  <div className="flex items-center">
                    <input
                      id="candlestickPatternFilter"
                      type="checkbox"
                      checked={filters.candlestickPatternFilter}
                      onChange={() => handleFilterChange('candlestickPatternFilter')}
                      className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                    />
                    <label htmlFor="candlestickPatternFilter" className="ml-3 text-sm font-medium text-gray-700">
                      Candlestick Pattern Filter
                    </label>
                  </div>
                </div>

                {/* Clear Filters Button */}
                <div className="mt-4 pt-4 border-t border-gray-200">
                  <button
                    onClick={() => setFilters({
                      volumeFilter: false,
                      rsiFilter: false,
                      engulfingFilter: false,
                      candlestickPatternFilter: false,
                      tickerFilter: ''
                    })}
                    className="w-full bg-gray-100 hover:bg-gray-200 text-gray-700 font-medium py-2 px-4 rounded text-sm mb-3"
                  >
                    Clear All Filters
                  </button>
                </div>

                {/* Results Counter */}
                <div className="mt-2 pt-4 border-t border-gray-200">
                  <div className="text-sm text-gray-600">
                    Showing <span className="font-medium">{filteredData.length}</span> of <span className="font-medium">{data.length}</span> triggers
                  </div>
                  {filters.tickerFilter.trim() && (
                    <div className="text-xs text-blue-600 mt-1">
                      Filtered by ticker: "{filters.tickerFilter}"
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default TriggersPage; 